<template>
  <div class="swiper">
    <div
      class="swiper-wrapper"
      :class="{ animation }"
      ref="wrapper"
    >
      <div
        class="swiper-slides"
        ref="slides"
        v-touch:start="handlerStart"
        v-touch:end="handlerEnd"
        @mouseover="handlerStart"
        @mouseleave="handlerEnd"
      >
        <div
          v-for="(item, i) in items"
          :key="i"
          class="swiper-slide"
          :style="itemStyle"
        >
          <slot :item="item" :is-active="i === currentSlide" :index="i" />
        </div>
      </div>
    </div>
    <template v-if="items.length > slidesToShowComputed">
      <vue-client-only>
        <div>
          <div class="arrows" v-if="arrows && adaptive.isDesktop">
            <div
              v-for="direction in ['left', 'right']"
              :key="direction"
              :class="[`arrow-${direction}`]"
              @click="direction == 'left' ? prevSlide() : nextSlide()"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill="#999999" d="M23 10.45L3.41357 10.45L10.1442 3.70742C10.5347 3.31691 10.5347 2.68339 10.1442 2.29288C9.7542 1.90237 9.12019 1.90237 8.73018 2.29288L0.293004 10.7301C0.246503 10.7766 0.204499 10.8281 0.167498 10.8836C0.151498 10.9076 0.140497 10.9341 0.126497 10.9596C0.108996 10.9911 0.0900047 11.0221 0.0760044 11.0556C0.0625041 11.0881 0.0544945 11.1226 0.0444943 11.1566C0.0359941 11.1851 0.025 11.2121 0.0194999 11.2421C-0.00650066 11.3711 -0.00650066 11.5046 0.0194999 11.6336C0.025 11.6636 0.0359941 11.6906 0.0444943 11.7196C0.0544945 11.7531 0.0625041 11.7876 0.0760043 11.8201C0.0900046 11.8541 0.108996 11.8846 0.126497 11.9161C0.140497 11.9416 0.151498 11.9681 0.167498 11.9921C0.204499 12.0476 0.246503 12.0991 0.293004 12.1456L8.73018 20.5828C8.92518 20.7778 9.18119 20.8758 9.43719 20.8758C9.6932 20.8758 9.9492 20.7778 10.1442 20.5828C10.5347 20.1918 10.5347 19.5592 10.1442 19.1682L3.41357 12.45L23 12.45C23.5525 12.45 24 11.9901 24 11.4381C24 10.8856 23.5525 10.45 23 10.45Z" />
              </svg>
            </div>
          </div>

          <div :class="['dots',{'dots-disabled': isIOS}]" v-if="dots">
            <div v-for="(dot, i) in dynamicDots" :key="i" :style="dot.style" @click="goToSlide(i)" />
          </div>
        </div>
      </vue-client-only>
    </template>
  </div>
</template>

<script>
import VueClientOnly from 'vue-client-only';
import { throttle } from 'throttle-debounce';
import IOSDetect from '@/utils/ios-detect';

/**
 * Возвращает число между min и max
 * на основании value 0 ~ 1
 */
const interpolate = (value, [min, max]) => {
  value = Math.abs(value);
  if (value > 1) value = 1;
  return min + (max - min) * value;
};

const easeOutQuad = t => t*(2-t)

export default {
  name: 'ui-swiper',
  components: {
    VueClientOnly,
  },
  inject: ['adaptive'],
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    dots: {
      type: Boolean,
      default: () => false,
    },
    arrows: {
      type: Boolean,
      default: () => false,
    },
    slidesToShow: {
      type: Number,
      default: 1,
    },
    slidesToScroll: {
      type: Number,
      default: null,
    },
    autoplay: {
      type: Boolean,
      default: false,
    }
  },

  data: () => ({
    isMoving: false,
    scrollPosition: 0, // 0 ~ 1
    animation: false,
    timeout: null,
    isIOS: false,
    currentSlide: 0,
  }),

  computed: {
    isSlidingNow() {
      return this.animation || this.isMoving;
    },
    itemStyle() {
      return {
        flex: `0 0 ${100/this.slidesToShow}%`,
      }
    },
    slidesToShowComputed() {
      return this.slidesToShow || this.slidesToScroll;
    },
    totalDots() {
      return Math.ceil(this.items.length / this.slidesToShowComputed);
    },
    currentDot() {
      // возвращает float, отсчет с 0
      return Math.ceil((this.totalDots - 1) * this.scrollPosition * 100) / 100;
    },
    dynamicDots() {
      return new Array(this.totalDots).fill().map((e, i) => ({
        style: {
          transform: `scale(${interpolate(i - this.currentDot, [1.35, 1])})`,
          opacity: interpolate(i - this.currentDot, [1, 0.4]),
        },
      }));
    },
    prevSlideIndex() {
      return Math.floor(this.currentDot) - 1;
    },
    nextSlideIndex() {
      return Math.ceil(this.currentDot) + 1;
    },
  },

  mounted() {
    this.isIOS = IOSDetect();
    this.initScrollListener();
    this.initAutoplay();
  },

  beforeDestroy() {
      this?.$refs?.slides?.removeEventListener?.('scroll', this.setScrollPosition);
  },

  methods: {
    handlerStart() {
      this.isMoving = true;
      this.stopAutoPlay();
    },
    handlerEnd() {
      this.isMoving = false;
      this.initAutoplay();
    },
    initScrollListener() {
      this.$refs.slides.addEventListener('scroll', this.setScrollPosition);
    },
    setScrollPosition: throttle(100, function() {
      const slides = this.$refs.slides;
      this.scrollPosition = slides.scrollLeft / (slides.scrollWidth - slides.offsetWidth);
      this.onChangeScrollPosition();
    }),
    prevSlide() {
      this.goToSlide(this.prevSlideIndex);
    },
    nextSlide() {
      if(Math.ceil(this.currentDot + 1) === this.totalDots) {
        this.goToSlide(0);
        return;
      }
      this.goToSlide(this.nextSlideIndex);
    },
    goToSlide(dotNumber) {
      this.stopAutoPlay();
      const targetPosition = this.$refs.slides.offsetWidth * dotNumber;
      this.animation = true;
      requestAnimationFrame(now => this.smooth(now, now, targetPosition));
    },
    smooth(start, now, targetPosition, duration = 500) {
      const position = this.$refs.slides.scrollLeft;
      const elapsed = now - start;
      const progress = Math.ceil(easeOutQuad(elapsed / 500) * 100) / 100;
      const scrollPos = position + (targetPosition - position) * progress;
      this.$refs.slides.scrollLeft = scrollPos;
      if ((progress < 1 && progress >= 0)) {
        requestAnimationFrame(now => this.smooth(start, now, targetPosition, duration));
      } else {
        this.animation = false;
        this.onChangeScrollPosition();
        this.initAutoplay();
      }
    },
    initAutoplay() {
      if(this.isIOS) return;
      if(this.autoplay) {
        this.timeout = setTimeout(() => {
          // если на тачах происходит скролл или курсор находится над баннером не переключаем слайд
          if (this.isSlidingNow) return;
          this.nextSlide();
        }, 4000);
      }
    },
    stopAutoPlay() {
      if(this.isIOS) return;
      clearTimeout(this.timeout);
    },
    onChangeScrollPosition: throttle(300, function() {
      if (this.animation) return;
      const currentSlide = Math.round(this.currentDot);
      if (this.currentSlide === currentSlide) return;
      this.currentSlide = currentSlide;
      this.$emit('change', this.currentSlide);
    }),
  },
};
</script>

<style lang="scss">
.swiper {
  position: relative;
  &-wrapper {
    margin: 0 (-$containerGapMobile);
    overflow: hidden;
    position: relative;
  }
  &-slides {
    display: flex;
    overflow-x: scroll;
    overflow-y: hidden;
    flex-wrap: nowrap;
    padding: 0 calc($containerGapMobile/2);
    padding-bottom: 50px;
    margin-bottom: -50px;
    scroll-padding: 0 calc($containerGapMobile/2);
    scroll-snap-type: x mandatory;
    > * {
      scroll-snap-align: start;
      padding: 0 calc($containerGapMobile/2);
    }
    .animation & {
      scroll-snap-type: none;
      > * {
        scroll-snap-align: initial;
      }
    }
    &::after {
      content: '';
      flex: 0 0 calc($containerGapMobile/2);
    }
  }
  &-slide {
    > img {
      // кейс, когда item картинка
      width: 100%;
    }
  }
  .dots {
    display: flex;
    justify-content: center;
    align-items: center;
    bottom: -28px;
    > * {
      width: 6px;
      height: 6px;
      background: $gray;
      margin: 16px;
      border-radius: 50%;
    }
  }
  .dots-disabled {
    pointer-events: none;
  }
  img {
    max-width: 100%;
  }
}
@include breakpoint("desktop") {
  .swiper {
    &-wrapper {
      display: grid;
      margin: 4px 0;
    }
    &-slides {
      margin: 0 -#{calc($containerGapMobile/2)};
      padding: 0;
      scroll-padding: 0;
      padding-bottom: 50px;
      margin-bottom: -50px;
      &::after {
        display: none;
      }
    }
    .arrows {
      position: absolute;
      top: calc(50% - 94px);
      left: -12px;
      right: -12px;
      > * {
        position: absolute;
        width: 48px;
        height: 48px;
        padding: 12px;
        background: $white;
        box-shadow: 0 0 0 1px $gray_lu;
        border-radius: 50%;
        cursor: pointer;
        &.disabled {
          pointer-events: none;
          svg {
            opacity: .5;
          }
        }
      }
      .arrow-right {
        right: 0;
        transform: rotate(180deg);
      }
    }
  }
}
</style>
